'use client';
function _array_like_to_array(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_without_holes(arr) {
    if (Array.isArray(arr)) return _array_like_to_array(arr);
}
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _iterable_to_array(iter) {
    if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _non_iterable_spread() {
    throw new TypeError("Invalid attempt to spread non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _to_consumable_array(arr) {
    return _array_without_holes(arr) || _iterable_to_array(arr) || _unsupported_iterable_to_array(arr) || _non_iterable_spread();
}
function _unsupported_iterable_to_array(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array(o, minLen);
}
import * as Sentry from "@sentry/core";
import IntlMessageFormat from "intl-messageformat";
import { createElement, useCallback } from "react";
import { IntlProvider, useIntl } from "react-intl";
import { Platform } from "react-native";
import { useAppContext } from "../components/AppContext";
import { Environment, environment, IS_PRODUCTION } from "../constants";
var INVALID_FLAG = '🚫';
var DEFAULT_LOCALE = 'en-US';
var FLAGS = {
    en: ' 🇺🇸',
    es: '🇲🇽'
};
export var LOCALES = Object.keys(FLAGS);
function wrapInFlags(text, locale) {
    var _FLAGS_locale, _ref;
    var flag = (_ref = (_FLAGS_locale = FLAGS[locale]) !== null && _FLAGS_locale !== void 0 ? _FLAGS_locale : FLAGS[locale.split('-')[0]]) !== null && _ref !== void 0 ? _ref : locale;
    return "".concat(flag).concat(text).concat(flag);
}
// In production we dont want to override $t to show our flags and we'd rather not take a perf hit
export var useI18n = IS_PRODUCTION ? useIntl : function() {
    var _useAppContext = useAppContext(), _useAppContext_flags = _useAppContext.flags, showI18NFlags = _useAppContext_flags.showI18NFlags, showI18NKeys = _useAppContext_flags.showI18NKeys;
    var intl = useIntl();
    var $t = useCallback(function() {
        for(var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++){
            args[_key] = arguments[_key];
        }
        var _intl;
        if (showI18NKeys) return args[0].id;
        // @ts-expect-error
        var result = (_intl = intl).formatMessage.apply(_intl, _to_consumable_array(args));
        var hasMessage = !!intl.messages[args[0].id];
        return showI18NFlags ? intl.locale === 'en' || hasMessage ? wrapInFlags(result, intl.locale) : "".concat(INVALID_FLAG).concat(result).concat(INVALID_FLAG) : result;
    }, [
        intl,
        showI18NFlags,
        showI18NKeys
    ]);
    return _object_spread_props(_object_spread({}, intl), {
        $t: $t
    });
};
export function formatTemplate(messageTemplate, variables, options) {
    var _options_locale;
    var template = new IntlMessageFormat(messageTemplate, (_options_locale = options === null || options === void 0 ? void 0 : options.locale) !== null && _options_locale !== void 0 ? _options_locale : DEFAULT_LOCALE);
    var result = template.format(variables);
    var _options_locale1;
    return (options === null || options === void 0 ? void 0 : options.wrapInFlags) ? wrapInFlags(result, (_options_locale1 = options === null || options === void 0 ? void 0 : options.locale) !== null && _options_locale1 !== void 0 ? _options_locale1 : DEFAULT_LOCALE) : result;
}
export function I18nProvider(param) {
    var children = param.children, getMessages = param.getMessages, _param_ignoreMissingMessages = param.ignoreMissingMessages, ignoreMissingMessages = _param_ignoreMissingMessages === void 0 ? true : _param_ignoreMissingMessages;
    var locale = useAppContext().locale;
    var forceDefaultMessages = locale.startsWith('en') && environment === Environment.DEVELOPMENT;
    return createElement(IntlProvider, {
        // not yet ready to serve other locales
        locale: environment === Environment.PRODUCTION ? 'en' : locale,
        messages: forceDefaultMessages ? {} : getMessages({
            locale: locale,
            lang: locale.split('-')[0]
        }),
        defaultLocale: 'en',
        fallbackOnEmptyString: false,
        onError: function(err) {
            if (ignoreMissingMessages) return;
            if (locale.startsWith('en') && environment !== Environment.PRODUCTION) {
                var _global_window;
                if (environment === Environment.DEVELOPMENT) return;
                if (Platform.OS === 'web' && ((_global_window = global.window) === null || _global_window === void 0 ? void 0 : _global_window.location.hostname.endsWith('.chromatic.com'))) return;
                Sentry.captureMessage('i18n warning for en locale', {
                    extra: {
                        err: err,
                        locale: locale
                    }
                });
            } else {
                Sentry.captureException(err, {
                    extra: {
                        locale: locale
                    }
                });
            }
        }
    }, children);
}
